import { Text } from '@atlaskit/primitives/compiled/text';

import InformationCircleIcon from '@atlaskit/icon/core/information-circle';

import Button from '@atlaskit/button/default/button';

import IconButton from '@atlaskit/button/icon/button';

import CrossIcon from '@atlaskit/icon/core/cross';

import { Box } from '@atlaskit/primitives/box';

import { Flex } from '@atlaskit/primitives/components/flex';

import { Inline } from '@atlaskit/primitives/inline';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { StepContextProvider, useStepContext } from '@post-office/message-step-context';

import { type MessageDescriptor, useIntl } from 'react-intl';

import { useState } from 'react';

import { registeredContent } from '../placements/in-app/screen-space-flags/messages';

import { cssMap } from '@atlaskit/css';

import { token } from '@atlaskit/tokens';

import { type JSX } from 'react/jsx-runtime';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

const messages = registeredContent.default;

const steps = [
	{
		headline: messages.step1Headline,
		bodyText: messages.step1Body,
	},
	{
		headline: messages.step2Headline,
		bodyText: messages.step2Body,
	},
	{
		headline: messages.step3Headline,
		bodyText: messages.step3Body,
	},
	{
		headline: messages.step4Headline,
		bodyText: messages.step4Body,
	},
];

const styles = cssMap({
	messageContainer: {
		paddingTop: token('space.150'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.300'),
		borderBottomColor: token('color.border'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
	},
});

const MessageStepProvider = ({
	isSpotlightVisible,
	onDismiss,
}: {
	isSpotlightVisible: boolean;
	onDismiss: (step: number, completed: boolean) => void;
}) => {
	return (
		<StepContextProvider steps={steps}>
			<MessageStepController isSpotlightVisible={isSpotlightVisible} onDismiss={onDismiss} />
		</StepContextProvider>
	);
};

const MessageStepController = ({
	isSpotlightVisible,
	onDismiss,
}: {
	isSpotlightVisible: boolean;
	onDismiss: (step: number, completed: boolean) => void;
}) => {
	const { currentStep } = useStepContext();
	const step = steps[currentStep - 1];

	if (!step || !isSpotlightVisible) return null;

	const renderInline = useMountToHtmlElement({
		selectors: `#rovo-agents-scenario-onboarding-step-${currentStep}`,
		as: 'inline',
		position: 'inside',
	});

	return (
		<div>
			{renderInline(
				<OnboardingSpotlight
					{...step}
					isSpotlightVisible={isSpotlightVisible}
					onDismiss={onDismiss}
				/>,
			)}
		</div>
	);
};

const OnboardingSpotlight = ({
	headline,
	bodyText,
	isSpotlightVisible,
	onDismiss,
}: {
	headline: MessageDescriptor;
	bodyText: MessageDescriptor;
	isSpotlightVisible: boolean;
	onDismiss: (step: number, completed: boolean) => void;
}) => {
	const { next, previous, currentStep, totalSteps } = useStepContext();
	const { formatMessage } = useIntl();
	const { onStepChanged } = usePostOfficeUserInteractions();

	const handleNext = () => {
		// Fire the backend event
		void onStepChanged({
			interactionIdentifier: 'next',
			direction: 'next',
			fromStep: currentStep,
			toStep: currentStep + 1,
		});

		next();
	};

	const handlePrevious = () => {
		// Fire the backend event
		void onStepChanged({
			interactionIdentifier: 'back',
			direction: 'previous',
			fromStep: currentStep,
			toStep: currentStep - 1,
		});

		previous();
	};

	const handleDismiss = () => {
		onDismiss(currentStep, false);
	};

	const handleComplete = () => {
		onDismiss(currentStep, true);
	};

	return (
		<PopoverProvider>
			<PopoverTarget>
				{/* Dummy target */}
				<div />
			</PopoverTarget>
			<PopoverContent
				dismiss={handleDismiss}
				placement="bottom-center"
				isVisible={isSpotlightVisible}
				shouldDismissOnClickOutside={false}
			>
				<SpotlightCard>
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(headline)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleDismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>{formatMessage(bodyText)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightStepCount>
							{formatMessage(messages.stepIndicator, { currentStep, totalSteps })}
						</SpotlightStepCount>
						<SpotlightActions>
							{currentStep > 1 && (
								<SpotlightSecondaryAction onClick={handlePrevious}>
									{formatMessage(messages.back)}
								</SpotlightSecondaryAction>
							)}
							{currentStep >= totalSteps ? (
								<SpotlightPrimaryAction onClick={handleComplete}>
									{formatMessage(messages.done)}
								</SpotlightPrimaryAction>
							) : (
								<SpotlightPrimaryAction onClick={handleNext}>
									{formatMessage(messages.next)}
								</SpotlightPrimaryAction>
							)}
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};

export const OnboardingBanner = ({
	onDismiss,
}: {
	onDismiss?: (step: number, completed: boolean) => void;
}): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [isVisible, setIsVisible] = useState(true);
	const [isSpotlightVisible, setIsSpotlightVisible] = useState(false);
	const { onCtaActioned, onAcknowledged, onClosed } = usePostOfficeUserInteractions();

	const handleStartTour = () => {
		setIsSpotlightVisible(true);

		// Fire the backend event
		void onCtaActioned({
			interactionIdentifier: 'startTour',
		});
	};

	const handleDismiss = (step: number, completed: boolean = false) => {
		setIsVisible(false);
		setIsSpotlightVisible(false);

		// Fire the backend event: completing the final step acknowledges the tour,
		// closing it any other way is a plain close.
		if (completed) {
			void onAcknowledged({
				interactionIdentifier: 'dismiss',
				additionalMetadata: { step, completed },
			});
		} else {
			void onClosed({
				interactionIdentifier: 'dismiss',
				additionalMetadata: { step, completed },
			});
		}

		// Call the parent's onDismiss callback if provided
		onDismiss?.(step, completed);
	};

	const handleDismissFromBanner = () => {
		handleDismiss(-1);
	};

	return isVisible ? (
		<Box>
			<Inline
				space="space.200"
				alignBlock="center"
				spread="space-between"
				xcss={styles.messageContainer}
			>
				<Flex alignItems="center" gap="space.100">
					<InformationCircleIcon label={formatMessage(messages.gettingStarted)} />
					<Text>{formatMessage(messages.gettingStarted)}</Text>
				</Flex>
				<Flex alignItems="center" gap="space.100">
					<Button onClick={handleStartTour}>{formatMessage(messages.startTour)}</Button>
					<IconButton
						icon={CrossIcon}
						label={formatMessage(messages.close)}
						isTooltipDisabled={false}
						onClick={handleDismissFromBanner}
					/>
				</Flex>
			</Inline>
			<MessageStepProvider isSpotlightVisible={isSpotlightVisible} onDismiss={handleDismiss} />
		</Box>
	) : null;
};
